import React, { useState } from 'react';
import BackButton from './BackButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import searchIcon from "../../../../../assets/icons/search.svg";
import VideoImage from "../../../../../assets/icons/videoBgImage.png";
import PlayButton from "../../../../../assets/icons/VideoPlaybutton.svg";
import VideoPreviewModal from '../../../../../common/components/VideoPreviewModal'; // Adjust the import path as needed

interface VideoCardProps {
  image: string;
  playButton: string;
  heading: string;
  // Optional onClick if you want to pass it down to VideoCard
  onClick?: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ image, playButton, heading, onClick }) => (
  <div
    onClick={onClick}
    className="flex-1 flex flex-col p-2 dark:bg-gray-800 rounded-xl border border-[#f0eff8] dark:border-gray-700 cursor-pointer hover:shadow-lg transition-shadow"
  >
    <div className="relative w-full h-44 bg-[#eef4fc] dark:bg-gray-700 rounded-lg mb-2">
      <img
        src={image}
        alt={`${heading} thumbnail`}
        className="absolute w-full h-full object-cover"
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src={playButton}
          alt={`Play ${heading}`}
          className="w-8 h-8 cursor-pointer hover:scale-110 transition-transform"
        />
      </div>
    </div>
    <h4 className="text-[14px] font-[600] leading-[21px] dark:text-white">
      {heading}
    </h4>
  </div>
);

const VideoSectionViewAll: React.FC = () => {
  // State to store search term, modal open state and selected video data
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    image: string;
    playButton: string;
    heading: string;
  } | null>(null);

  // Handler to update search term
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Open modal with selected video details
  const handleVideoClick = (video: { image: string; playButton: string; heading: string; }) => {
    setSelectedVideo(video);
    setOpenModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedVideo(null);
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1108px',
    padding: '0',
  };

  const videos = [
    {
      image: VideoImage,
      playButton: PlayButton,
      heading: 'Video Heading 1'
    },
    {
      image: VideoImage,
      playButton: PlayButton,
      heading: 'Video Heading 2'
    },
    {
      image: VideoImage,
      playButton: PlayButton,
      heading: 'Video Heading 3'
    },
    {
      image: VideoImage,
      playButton: PlayButton,
      heading: 'Video Heading 4'
    },
    {
      image: VideoImage,
      playButton: PlayButton,
      heading: 'Video Heading 5'
    },
    {
      image: VideoImage,
      playButton: PlayButton,
      heading: 'Video Heading 6'
    }
  ];

  // Filter videos by checking if the heading includes the search term (case-insensitive)
  const filteredVideos = videos.filter(video =>
    video.heading.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col w-full max-w-none">
      <BackButton />

      {/* Title + Search Header */}
      <div style={containerStyle}>
        <h4 className="text-[24px] font-semibold leading-[36px]">
          Videos
        </h4>

        {/* Search shifted to right */}
        <div style={{ marginLeft: 'auto' }}>
          <TextField
            variant="outlined"
            placeholder="Search"
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img
                    src={searchIcon}
                    alt="search"
                    style={{
                      width: '16px',
                      height: '16px',
                      objectFit: 'contain',
                    }}
                  />
                </InputAdornment>
              ),
              style: {
                height: '48px',
                paddingRight: '16px',
                paddingLeft: '16px',
                fontSize: '14px',
                borderRadius: '8px',
              },
            }}
            sx={{
              width: '400px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
                paddingRight: '8px',
                '& fieldset': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#808080',
                opacity: 1,
              },
            }}
          />
        </div>
      </div>

      {/* Video Cards Grid: Responsive grid */}
      {filteredVideos.length === 0 ? (
        <div className="mt-8 text-center text-gray-500 dark:text-gray-300">
          No videos found.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {filteredVideos.map((video, index) => (
            <VideoCard
              key={index}
              {...video}
              onClick={() => handleVideoClick(video)}
            />
          ))}
        </div>
      )}

      {/* Render VideoPreviewModal if open */}
      {openModal && selectedVideo && (
        <VideoPreviewModal
          title={selectedVideo.heading}
          videoUrl={selectedVideo.image} // Change to the actual video URL if available
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default VideoSectionViewAll;
